import { useEffect, useRef } from 'react';

// Accesibilidad de diálogos modales (WCAG 2.1.1 Teclado, 2.4.3 Orden del foco; patrón APG "dialog").
// Un <div role="dialog" aria-modal="true"> sin gestión de foco es peor que nada: el lector de pantalla
// poda el fondo pero el teclado sí puede tabular hacia él. Este hook, aplicado al elemento-tarjeta del
// diálogo, resuelve las 4 obligaciones del patrón:
//   1. enfoca el primer control al abrir (o la propia tarjeta si no hay ninguno),
//   2. Escape cierra,
//   3. atrapa Tab / Shift+Tab dentro del diálogo (no se escapa al fondo),
//   4. restaura el foco al disparador al cerrar.
//
// Uso:
//   const cardRef = useModalDialog(onClose);
//   <div className="X-modal" role="dialog" aria-modal="true">
//     <div className="X-modal__overlay" onClick={onClose} aria-hidden="true" />
//     <div className="X-modal__card" ref={cardRef}> … </div>
//   </div>

const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
    'input:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

const visible = (el) => el.getClientRects().length > 0;

export function useModalDialog(onClose) {
    const ref = useRef(null);
    // Ref al último onClose para no re-suscribir el listener en cada render.
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    useEffect(() => {
        const node = ref.current;
        const opener = document.activeElement; // disparador, para devolver el foco al cerrar

        if (node) {
            const first = [...node.querySelectorAll(FOCUSABLE)].find(visible);
            if (first) first.focus();
            else { node.setAttribute('tabindex', '-1'); node.focus(); }
        }

        const onKeyDown = (e) => {
            if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current?.(); return; }
            if (e.key !== 'Tab' || !node) return;
            const items = [...node.querySelectorAll(FOCUSABLE)].filter(visible);
            if (!items.length) return;
            const firstEl = items[0];
            const lastEl = items[items.length - 1];
            const active = document.activeElement;
            // Solo atrapamos si el foco está dentro del diálogo (si no, dejamos pasar).
            if (!node.contains(active)) { e.preventDefault(); firstEl.focus(); return; }
            if (e.shiftKey && active === firstEl) { e.preventDefault(); lastEl.focus(); }
            else if (!e.shiftKey && active === lastEl) { e.preventDefault(); firstEl.focus(); }
        };

        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            if (opener && typeof opener.focus === 'function') opener.focus();
        };
    }, []);

    return ref;
}
